import type { StyleSpecification } from 'maplibre-gl'

/**
 * Basemap catalogue.
 *
 * The self-hosted vector archive stays the default and is the only one that works with no
 * internet -- that property is the whole reason `make tiles` exists, and nothing here is
 * allowed to erode it. The raster entries below are additions for the cases the extract
 * cannot serve: checking a hub against actual imagery, reading terrain beside the ghat
 * section, or panning outside the corridor bbox the archive was cut to.
 *
 * Every one of these is somebody else's tile server, fetched directly by the operator's
 * browser:
 *
 *   - they need internet, so they are useless on a depot machine that has none;
 *   - they are courtesy endpoints under usage policies (OSM's in particular forbids heavy
 *     or automated use), fine for a handful of operator sessions and not fine as the
 *     default for a wall display that pans all day;
 *   - their attribution is a licence condition, not decoration, which is why every entry
 *     carries an `attribution` string that MapLibre renders in the corner.
 *
 * Point `tiles` at your own proxy if you want any of this on a real deployment.
 */

export type BasemapMode =
  | 'vector' | 'osm' | 'satellite' | 'terrain' | 'photoreal' | 'google2d'

/** Modes that are a remote raster tile service rather than the local archive. */
export type RasterMode = Exclude<BasemapMode, 'vector' | 'photoreal'>

export type RasterBasemap = {
  id: RasterMode
  label: string
  hint: string
  tiles: string[]
  attribution: string
  /** Deepest zoom the service publishes. Past it MapLibre overzooms rather than 404s. */
  maxzoom: number
  /** Whether the imagery reads dark, which picks the sprite set and the pre-load canvas. */
  dark: boolean
  /** Shown behind the tiles while they load, so the gap does not flash the wrong colour. */
  background: string
}

export const RASTER_BASEMAPS: RasterBasemap[] = [
  {
    id: 'osm',
    label: 'openstreetmap',
    hint: 'OpenStreetMap standard raster. Needs internet. Community tile servers -- fine for a few operator sessions, not for a wall display left panning all day.',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    attribution: '<a href="https://openstreetmap.org/copyright">© OpenStreetMap contributors</a>',
    maxzoom: 19,
    dark: false,
    background: '#F2EFE9',
  },
  {
    id: 'satellite',
    label: 'satellite',
    hint: 'Esri World Imagery. Needs internet. The one worth switching to when a hub polygon looks wrong and you want to see the actual yard.',
    tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'Imagery © <a href="https://www.esri.com">Esri</a>, Maxar, Earthstar Geographics',
    maxzoom: 19,
    dark: true,
    background: '#0B1017',
  },
  {
    id: 'terrain',
    label: 'terrain',
    hint: 'OpenTopoMap: contours and hillshade. Needs internet. Useful for reading the ghat gradients behind an energy or overspeed alert.',
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
    ],
    attribution: '© <a href="https://openstreetmap.org/copyright">OpenStreetMap contributors</a>, SRTM | © <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxzoom: 17,
    dark: false,
    background: '#EAE7DE',
  },
]

const BY_ID = new Map(RASTER_BASEMAPS.map((b) => [b.id, b]))

/**
 * Google's 2D Map Tiles API, NOT a RASTER_BASEMAPS literal -- unlike OSM/Esri/OpenTopoMap/
 * CARTO, this one needs a session token (POST /v1/createSession, keyed to mapType/language/
 * region) before a single tile URL can even be constructed, and the token expires. Cached
 * here once created via `registerGoogle2dTiles`; `rasterBasemap('google2d')` returns null
 * until then, which `style.ts::buildStyle` already treats as "fall through to vector" --
 * so this mode is only ever selectable once BasemapSwitch.tsx has confirmed a session
 * exists (see its `google2dAvailable` gate), never mid-flight with nothing to render.
 *
 * Attribution: Map Tiles API tiles carry NO baked-in logo (unlike embedded Maps JS API
 * widgets) -- Google's own policy requires the developer to display one. Full logo is the
 * default; their policy explicitly allows plain "Google Maps" text in space-constrained UI,
 * which is what this basemap's `attribution` string uses, matching every other entry here
 * (MapLibre renders `attribution` in the corner control already).
 */
let _google2d: RasterBasemap | null = null

export function registerGoogle2dTiles(session: string, apiKey: string): void {
  _google2d = {
    id: 'google2d',
    label: 'google maps',
    hint: 'Google Maps roadmap tiles. Billed per session, needs internet.',
    tiles: [`https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${apiKey}`],
    attribution: 'Google Maps',
    maxzoom: 22,
    dark: false,
    background: '#E8EAED',
  }
}

export function rasterBasemap(mode: BasemapMode): RasterBasemap | null {
  if (mode === 'google2d') return _google2d
  return BY_ID.get(mode as RasterMode) ?? null
}

/**
 * Photoreal is an overlay drawn on top of the vector style, not a style of its own, so it
 * resolves to the same base. Used as the restyle key: without the collapse, toggling 3D
 * would tear down and rebuild a style that is not actually changing.
 */
export function styleKey(mode: BasemapMode): string {
  return mode === 'photoreal' ? 'vector' : mode
}

/**
 * Same reasoning as style.ts: absolute URLs, because MapLibre's worker resolves them
 * against the asset directory rather than the page.
 */
const origin = (): string => (typeof location === 'undefined' ? '' : location.origin)

/**
 * A raster style carrying the same glyphs and sprites as the vector one -- the reference,
 * fleet and heat layers are re-added on top of whatever style is current, and they use
 * both. Dropping either would leave every label as a box and every marker missing.
 */
export function buildRasterStyle(b: RasterBasemap): StyleSpecification {
  return {
    version: 8,
    glyphs: `${origin()}/tiles/fonts/{fontstack}/{range}.pbf`,
    sprite: `${origin()}/tiles/sprites/${b.dark ? 'dark' : 'light'}`,
    sources: {
      basemap: {
        type: 'raster',
        tiles: b.tiles,
        tileSize: 256,
        maxzoom: b.maxzoom,
        attribution: b.attribution,
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': b.background } },
      { id: 'basemap', type: 'raster', source: 'basemap' },
    ],
  }
}

/**
 * Creates the Map Tiles API session and registers the resulting tile URL via
 * `registerGoogle2dTiles`. One session per page load, not per tile -- the token is valid
 * for the response's `expiry` (weeks, per Google's docs), so there is no per-tile
 * round-trip the way `addPhotoreal`'s 3D tiles have per-tile fetches; this is a single
 * setup call. Called from MapCanvas.tsx once `mapsKey` is known (same key already used
 * for photoreal 3D -- one GOOGLE_MAPS_API_KEY, two products on the same Maps Platform
 * project), fire-and-forget: a failure here just leaves 'google2d' unavailable, same as
 * an unset key does, not a page-level error.
 */
export async function createGoogle2dSession(apiKey: string): Promise<boolean> {
  try {
    const res = await fetch(`https://tile.googleapis.com/v1/createSession?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mapType: 'roadmap', language: 'en-IN', region: 'IN' }),
    })
    if (!res.ok) return false
    const body = (await res.json()) as { session?: string }
    if (!body.session) return false
    registerGoogle2dTiles(body.session, apiKey)
    return true
  } catch {
    return false
  }
}
